import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { boFetch } from "@/lib/backoffice.server";

const dateRe = /^\d{4}-\d{2}-\d{2}$/;
const timeRe = /^\d{2}:\d{2}$/;

export type AvailabilityRange = { start: string; end: string };

export type DayAvailability = {
  taken: string[];
  available: AvailabilityRange[] | null;
  hasSchedule: boolean;
  fullDayBlocked: boolean;
  blocked: AvailabilityRange[];
};

export const getTakenSlots = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ date: z.string().regex(dateRe) }).parse(input))
  .handler(async ({ data }): Promise<DayAvailability> => {
    try {
      const res = await boFetch<Partial<DayAvailability>>("/api/public/v1/bookings/slots", {
        query: { date: data.date },
        // Availability must always be fresh — never serve from the edge cache.
        cache: false,
      });
      return {
        taken: res.taken ?? [],
        available: res.available ?? null,
        hasSchedule: res.hasSchedule === true,
        fullDayBlocked: res.fullDayBlocked === true,
        blocked: res.blocked ?? [],
      };
    } catch (e) {
      console.error("getTakenSlots failed", e);
      return { taken: [], available: null, hasSchedule: false, fullDayBlocked: false, blocked: [] };
    }
  });

function extractFieldErrors(payload: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!payload || typeof payload !== "object") return out;
  const p = payload as Record<string, unknown>;
  const src = p.fieldErrors ?? p.errors ?? p.details;
  if (Array.isArray(src)) {
    for (const item of src as Array<Record<string, unknown>>) {
      const field = item?.field ?? (Array.isArray(item?.path) ? item.path[0] : item?.path);
      const msg = item?.message ?? item?.msg;
      if (field && typeof msg === "string") out[String(field)] = msg;
    }
  } else if (src && typeof src === "object") {
    for (const [k, v] of Object.entries(src as Record<string, unknown>)) {
      if (typeof v === "string") out[k] = v;
      else if (Array.isArray(v) && typeof v[0] === "string") out[k] = v[0];
    }
  }
  return out;
}

/**
 * Creates the booking in the backoffice and attempts to start a Stripe
 * Checkout session. Stripe Connect onboarding is still pending on the
 * backoffice side, so checkout currently throws a "no disponible" error
 * — the booking is still persisted as "pending" so the despacho can
 * follow up manually.
 */
export const createBookingCheckout = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z
      .object({
        productId: z.string().min(1).max(200),
        date: z.string().regex(dateRe),
        start: z.string().regex(timeRe),
        end: z.string().regex(timeRe),
        fullName: z.string().trim().min(2).max(120),
        email: z.string().trim().email().max(180),
        phone: z.string().trim().min(6).max(40),
        reason: z.string().trim().min(20).max(2000),
        returnUrl: z.string().url(),
        environment: z.enum(["sandbox", "live"]),
        existingClient: z.boolean().optional(),
        marketingConsent: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    let booking: { booking: { id: string } };
    try {
      booking = await boFetch<{ booking: { id: string } }>("/api/public/v1/bookings", {
        method: "POST",
        body: {
          productId: data.productId,
          date: data.date,
          start: data.start,
          end: data.end,
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
          reason: data.reason,
          existingClient: data.existingClient === true,
          marketingConsent: data.marketingConsent === true,
        },
      });
    } catch (e) {
      const err = e as { status?: number; payload?: unknown; message?: string };
      // 409 — slot was taken between selection and submit.
      if (err.status === 409) return { ok: false as const, code: "conflict" as const };
      // 410 — the product/service is no longer offered.
      if (err.status === 410) return { ok: false as const, code: "gone" as const };
      // 400 — validation error; surface per-field messages.
      if (err.status === 400) {
        return {
          ok: false as const,
          code: "validation" as const,
          fieldErrors: extractFieldErrors(err.payload),
        };
      }
      throw e;
    }

    // Existing clients with an ongoing case skip payment — booking is
    // persisted and the despacho confirms manually via phone.
    if (data.existingClient) {
      return {
        ok: true as const,
        clientSecret: null as string | null,
        bookingId: booking.booking.id,
        pendingConfirmation: true,
      };
    }

    try {
      const session = await boFetch<{ clientSecret: string }>("/api/public/v1/checkout/session", {
        method: "POST",
        body: {
          type: "booking",
          bookingId: booking.booking.id,
          returnUrl: data.returnUrl,
          environment: data.environment,
        },
      });
      return {
        ok: true as const,
        clientSecret: session.clientSecret as string | null,
        bookingId: booking.booking.id,
        pendingConfirmation: false,
      };
    } catch (e) {
      const err = e as { status?: number; message?: string };
      if (err.status === 501 || err.status === 503) {
        return {
          ok: true as const,
          clientSecret: null as string | null,
          bookingId: booking.booking.id,
          pendingConfirmation: true,
        };
      }
      throw e;
    }
  });


export const getBookingBySession = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ sessionId: z.string().min(5).max(200) }).parse(input))
  .handler(async () => {
    // Stripe Connect checkout not yet wired in backoffice → no session lookup.
    return { booking: null as { status?: string } | null };
  });
